import {AnalyticsEvent} from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FocusedTableViewMode} from 'notebook/tables/TableConstants';

import {
  ANALYSIS_TOOL_ANALYTICS_ACTIONS,
  AUDIT_ANALYTICS_ACTIONS,
  BENCHLING_APPS_ANALYTICS_ACTIONS,
  BLAST_ANALYTICS_ACTIONS,
  CAPILLARY_ANALYTICS_ACTIONS,
  CHEMISTRY_ANALYTICS_ACTIONS,
  COLLABORATION_ANALYTICS_ACTIONS,
  CONFIG_MIGRATION_ANALYTICS_ACTIONS,
  DATA_FRAMES_ANALYTICS_ACTIONS,
  ELN_ANALYTICS_ACTIONS,
  EVENTS_ANALYTICS_ACTIONS,
  FORMS_ANALYTICS_ACTIONS,
  GROWTH_ANALYTICS_ACTIONS,
  IDENTITY_ANALYTICS_ACTIONS,
  INSIGHTS_ANALYTICS_ACTIONS,
  LEGACY_WORKFLOWS_ANALYTICS_ACTIONS,
  MODBIO_ANALYTICS_ACTIONS,
  MOLBIO_ANALYTICS_ACTIONS,
  NAV_ANALYTICS_ACTIONS,
  PERMISSION_CONTROLS_ANALYTICS_ACTIONS,
  PROCEDURES_ANALYTICS_ACTIONS,
  REACTION_ANALYTICS_ACTIONS,
  REGISTRY_ANALYTICS_ACTIONS,
  REQUESTS_ANALYTICS_ACTIONS,
  RESULTS_ANALYTICS_ACTIONS,
  REVIEWS_ANALYTICS_ACTIONS,
  SCHEMAS_ANALYTICS_ACTIONS,
  SEARCH_ANALYTICS_ACTIONS,
  STORABLE_ANALYTICS_ACTIONS,
  STUDIES_ANALYTICS_ACTIONS,
  TENANT_COMPARISON_ANALYTICS_ACTIONS,
  WORKFLOWS_ANALYTICS_ACTIONS,
  WORKLISTS_ANALYTICS_ACTIONS,
} from './analytics-products';

export {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
export {ActionMethod, ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
export * from './analytics-products';

export interface BaseAnalyticsMetadata {
  actionOrigin: ActionOrigin;
  eventGroupId: string;
}

export enum CSV_TYPE {
  PLATE_RECORD = 'plate_transfer_record',
  PLATE_DESIGN = 'plate_design',
}

export enum CloseType {
  CANCEL = 'CANCEL',
  EXIT = 'EXIT',
}

export interface TableViewsMetadata {
  isMaximized: boolean;
  focusedTableViewMode: FocusedTableViewMode;
  structured_table_kind?: string;
  structured_table_api_identifier?: string;
  structured_table_num_rows?: number;
  structured_table_num_columns?: number;
}

/**
 * These are actions that we want to track across all analytics events.
 * In general, the `action_origin` metadata field should be used to specify
 * what product/module these common actions originate from.
 */
export enum CommonAnalyticsActions {
  // Modal actions to be used for all new modal analytics and old ones will be
  // converted to over time; metadata should use `modal_type` to specify the type of
  // modal being opened, closed, or submitted
  OPENED_MODAL = 'OPENED_MODAL',
  CLOSED_MODAL = 'CLOSED_MODAL',
  SUBMITTED_MODAL = 'SUBMITTED_MODAL',

  SUBMITTED_CREATE_ANALYSIS = 'SUBMITTED_CREATE_ANALYSIS',
  SUBMITTED_BETA_REQUEST = 'SUBMITTED_BETA_REQUEST',

  // Internal tab actions should take advantage of the `action_origin` metadata field to
  //  specify the product using these. Any additional information can be added as metadata.
  NAVIGATED_TAB = 'NAVIGATED_TAB',

  // Events related to errors.
  TRIGGERED_ERROR_TOAST = 'TRIGGERED_ERROR_TOAST',

  // Actions related to any dropdown usage in the application
  //  (e.g. opening a dropdown, selecting a dropdown option, etc.).
  // You will want to specify dropdown option types
  OPENED_DROPDOWN = 'OPENED_DROPDOWN',
  SELECTED_DROPDOWN_OPTION = 'SELECTED_DROPDOWN_OPTION',

  // Actions related to Stepper/Wizards/Progress Steps and other components with guided flows made of steps.
  //  Use with the `NavigatedStepperEventMetadata` metadata
  NAVIGATED_STEP_COMPONENT = 'NAVIGATED_STEP_COMPONENT',

  VIEWED_WORKSPACE_ITEM = 'VIEWED_WORKSPACE_ITEM',
  DOWNLOADED_CSV = 'DOWNLOADED_CSV',
}

export const COMMON_ANALYTICS_ACTIONS: {[key in CommonAnalyticsActions]: AnalyticsEvent} = {
  // Modal actions
  OPENED_MODAL: {
    module: ANALYTICS_MODULE.MODULE_MISSING,
    action: 'opened_modal',
    actionCategory: ACTION_CATEGORY.READ,
  },
  CLOSED_MODAL: {
    module: ANALYTICS_MODULE.MODULE_MISSING,
    action: 'closed_modal',
    actionCategory: ACTION_CATEGORY.READ,
  },
  SUBMITTED_MODAL: {
    module: ANALYTICS_MODULE.MODULE_MISSING,
    action: 'submitted_modal',
    actionCategory: ACTION_CATEGORY.WRITE,
  },
  SUBMITTED_CREATE_ANALYSIS: {
    module: ANALYTICS_MODULE.MODULE_MISSING,
    action: 'submitted_create_analysis',
    actionCategory: ACTION_CATEGORY.WRITE,
  },
  SUBMITTED_BETA_REQUEST: {
    module: ANALYTICS_MODULE.MODULE_MISSING,
    action: 'submitted_beta_request',
    actionCategory: ACTION_CATEGORY.WRITE,
  },
  // Tab actions
  NAVIGATED_TAB: {
    module: ANALYTICS_MODULE.MODULE_MISSING,
    action: CommonAnalyticsActions.NAVIGATED_TAB,
    actionCategory: ACTION_CATEGORY.READ,
  },
  // Error actions
  TRIGGERED_ERROR_TOAST: {
    module: ANALYTICS_MODULE.MODULE_MISSING,
    action: CommonAnalyticsActions.TRIGGERED_ERROR_TOAST,
    actionCategory: ACTION_CATEGORY.READ,
  },
  // Dropdown actions
  OPENED_DROPDOWN: {
    module: ANALYTICS_MODULE.MODULE_MISSING,
    action: CommonAnalyticsActions.OPENED_DROPDOWN,
    actionCategory: ACTION_CATEGORY.READ,
  },
  SELECTED_DROPDOWN_OPTION: {
    module: ANALYTICS_MODULE.MODULE_MISSING,
    action: CommonAnalyticsActions.SELECTED_DROPDOWN_OPTION,
    actionCategory: ACTION_CATEGORY.READ,
  },
  // Stepper actions
  NAVIGATED_STEP_COMPONENT: {
    module: ANALYTICS_MODULE.MODULE_MISSING,
    action: CommonAnalyticsActions.NAVIGATED_STEP_COMPONENT,
    actionCategory: ACTION_CATEGORY.READ,
  },
  VIEWED_WORKSPACE_ITEM: {
    module: ANALYTICS_MODULE.MODULE_MISSING,
    action: CommonAnalyticsActions.VIEWED_WORKSPACE_ITEM,
    actionCategory: ACTION_CATEGORY.READ,
  },
  DOWNLOADED_CSV: {
    module: ANALYTICS_MODULE.MODULE_MISSING,
    action: CommonAnalyticsActions.DOWNLOADED_CSV,
    actionCategory: ACTION_CATEGORY.READ,
  },
};

// This is a collection of all analytic actions.
// It is used to verify that all created events' action names are unique.
//
// NOTE: If a new product is added, it should be included in this object.
export const ALL_ANALYTICS_ACTIONS = {
  ANALYSIS_TOOL_ANALYTICS_ACTIONS,
  AUDIT_ANALYTICS_ACTIONS,
  BENCHLING_APPS_ANALYTICS_ACTIONS,
  BLAST_ANALYTICS_ACTIONS,
  CAPILLARY_ANALYTICS_ACTIONS,
  CHEMISTRY_ANALYTICS_ACTIONS,
  COLLABORATION_ANALYTICS_ACTIONS,
  COMMON_ANALYTICS_ACTIONS,
  CONFIG_MIGRATION_ANALYTICS_ACTIONS,
  DATA_FRAMES_ANALYTICS_ACTIONS,
  ELN_ANALYTICS_ACTIONS,
  EVENTS_ANALYTICS_ACTIONS,
  FORMS_ANALYTICS_ACTIONS,
  GROWTH_ANALYTICS_ACTIONS,
  IDENTITY_ANALYTICS_ACTIONS,
  INSIGHTS_ANALYTICS_ACTIONS,
  LEGACY_WORKFLOWS_ANALYTICS_ACTIONS,
  MODBIO_ANALYTICS_ACTIONS,
  MOLBIO_ANALYTICS_ACTIONS,
  NAV_ANALYTICS_ACTIONS,
  PERMISSION_CONTROLS_ANALYTICS_ACTIONS,
  PROCEDURES_ANALYTICS_ACTIONS,
  REACTION_ANALYTICS_ACTIONS,
  REGISTRY_ANALYTICS_ACTIONS,
  REQUESTS_ANALYTICS_ACTIONS,
  RESULTS_ANALYTICS_ACTIONS,
  REVIEWS_ANALYTICS_ACTIONS,
  SCHEMAS_ANALYTICS_ACTIONS,
  SEARCH_ANALYTICS_ACTIONS,
  STORABLE_ANALYTICS_ACTIONS,
  STUDIES_ANALYTICS_ACTIONS,
  TENANT_COMPARISON_ANALYTICS_ACTIONS,
  WORKFLOWS_ANALYTICS_ACTIONS,
  WORKLISTS_ANALYTICS_ACTIONS,
};

export const X_BENCH_ANALYTICS_METADATA = 'X-BENCH-ANALYTICS-METADATA';
